import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6cvfsjv53dtlm7j8bhg';

export default function FaqAskQuestion() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const question = formData.get('question') as string;

    if (!name?.trim() || !email?.trim() || !question?.trim()) return;
    if (question.length > 500) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name.trim());
      body.append('email', email.trim());
      body.append('question', question.trim());

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setIsSubmitted(true);
      form.reset();
    } catch {
      // Silent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-stone-100 py-16 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <h4 className="text-2xl font-bold text-[#1A2332] mb-3">
          <a
            href="#ask-question"
            className="hover:text-teal-600 transition-colors cursor-pointer"
          >
            Didn&apos;t Find Your Answer?
          </a>
        </h4>
        <p className="text-sm text-[#374151] mb-8 max-w-xl mx-auto">
          Submit your question and our Warrenton team will get back to you within
          24 hours. We serve Astoria, Seaside, Tillamook, and the entire Pacific
          Northwest.
        </p>

        {isSubmitted ? (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-8 animate-[fadeInUp_0.4s_ease-out]" role="status" aria-live="polite">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-teal-100 rounded-full">
              <i className="ri-check-line text-teal-600 text-xl" aria-hidden="true"></i>
            </div>
            <p className="text-teal-800 font-semibold text-sm mb-1">
              Question Submitted!
            </p>
            <p className="text-teal-700 text-xs">
              We&apos;ll respond within 24 hours. Call (855) 468-6348 for immediate
              help.
            </p>
          </div>
        ) : (
          <form
            id="faq-question-form"
            data-readdy-form=""
            onSubmit={handleSubmit}
            className="bg-white rounded-lg p-6 sm:p-8 shadow-sm border border-gray-100 text-left"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="faq-name"
                  className="block text-xs font-medium text-[#374151] mb-1.5"
                >
                  Your Name
                </label>
                <input
                  id="faq-name"
                  name="name"
                  type="text"
                  required
                  aria-required="true"
                  placeholder="John Smith"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="faq-email"
                  className="block text-xs font-medium text-[#374151] mb-1.5"
                >
                  Email Address
                </label>
                <input
                  id="faq-email"
                  name="email"
                  type="email"
                  required
                  aria-required="true"
                  placeholder="john@example.com"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mb-5">
              <label
                htmlFor="faq-question"
                className="block text-xs font-medium text-[#374151] mb-1.5"
              >
                Your Question
              </label>
              <textarea
                id="faq-question"
                name="question"
                required
                aria-required="true"
                aria-describedby="faq-question-hint"
                maxLength={500}
                rows={4}
                placeholder="What would you like to know about buying a car in the Pacific Northwest?"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              />
              <p id="faq-question-hint" className="text-xs text-[#4A5568] mt-1">Max 500 characters</p>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-teal-600 text-white px-8 py-3 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Question'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
